/**
 * Remark plugin that promotes a "title paragraph" immediately above a
 * GFM table into a `data-table-title` attribute on the table itself.
 *
 * Grammar the plugin recognises:
 *
 *   **Title**
 *
 *   | col | col |
 *   |-----|-----|
 *   | …   | …   |
 *
 * i.e. a paragraph whose *only* meaningful content is a single `strong`
 * node, sitting directly above a `table` node in the same parent's
 * children list. The plugin then:
 *
 *   1. Stamps the title text onto the table via
 *      `data.hProperties['data-table-title']`, which surfaces as a
 *      `data-table-title="…"` attribute on the rendered `<table>`.
 *   2. Removes the title paragraph so it doesn't render as a duplicate
 *      bold line above the table.
 *
 * The `ChatTable` React override (`upright-chat-markdown.tsx`) reads the
 * attribute and renders the title in a dedicated title-row, alongside
 * the copy + download icon buttons (PLT-4835).
 *
 * Why this shape:
 *   - The system prompt tells the model to always emit a bold one-line
 *     title directly above every table. Bold text is the smallest
 *     authoring imposition we can make — it's a single markdown token
 *     pair the model is already comfortable producing, and on a client
 *     that hasn't shipped this plugin yet the title still renders as a
 *     normal bolded line above the table. Graceful degradation.
 *   - Until the trailing `**` and the newline-blank-line-table sequence
 *     is in the streaming buffer, the paragraph stays unmatched and
 *     simply renders as a normal bolded paragraph. As soon as the
 *     trailing strong + blank line + table-header pipe arrive, the
 *     plugin matches and hoists the title. There is no buffering — the
 *     pass runs every render and is idempotent (`children.splice` once,
 *     then on the next pass there's no preceding paragraph to remove).
 *   - The `ChatTable` component supplies a `"Table"` fallback when the
 *     attribute is absent, so a model that forgets the title still
 *     produces a usable export action; the title row is never empty.
 *
 * Plugin order (declared in `upright-chat-markdown.tsx`): this plugin
 * is independent of the other table plugin (`remark-table-sign-color`)
 * — they touch different parts of the AST (this one mutates the
 * `table.data` and the parent's child array; the sign-color plugin
 * mutates cell children). Their relative order is don't-care.
 */
import { SKIP, visit } from 'unist-util-visit';
import type { Plugin } from 'unified';
import type { Paragraph, Root, Table } from 'mdast';

/** The hProperties key the plugin stamps onto a matched table. */
export const TABLE_TITLE_DATA_ATTR = 'data-table-title';

/**
 * Extract the title text from a paragraph if (and only if) it looks
 * like a "title paragraph" — a paragraph whose *only* meaningful child
 * is a single `strong` node, with non-blank text.
 *
 * Returns the trimmed title string, or `null` if the paragraph does
 * not match.
 *
 * Exported so a unit spec can pin the matching rules independently of
 * the AST walk.
 */
export function extractTitleFromParagraph(paragraph: Paragraph): string | null {
  // Filter out empty / whitespace-only text nodes — `remarkParse` can
  // emit them as siblings of inline nodes (e.g. trailing newlines).
  const meaningful = paragraph.children.filter(child => !(child.type === 'text' && child.value.trim().length === 0));
  if (meaningful.length !== 1) return null;
  const only = meaningful[0];
  if (only.type !== 'strong') return null;
  const text = strongToText(only.children);
  const trimmed = text.trim();
  if (trimmed.length === 0) return null;
  return trimmed;
}

/**
 * Best-effort plain-text extraction from a `strong` node's children.
 * Supports plain text nodes and a single nested level of emphasis /
 * strong / inline-code so titles like `**Net impact — \`FY24\`**` keep
 * their visible label intact.
 */
function strongToText(children: Paragraph['children']): string {
  let s = '';
  for (const child of children) {
    if (child.type === 'text') {
      s += child.value;
    } else if (child.type === 'inlineCode') {
      s += child.value;
    } else if (child.type === 'emphasis' || child.type === 'strong') {
      // Nested emphasis/strong inside the title bold — collapse to its
      // text content.
      for (const nested of child.children) {
        if (nested.type === 'text') s += nested.value;
        else if (nested.type === 'inlineCode') s += nested.value;
      }
    }
  }
  return s;
}

/** Set `data-table-title` on the table's hProperties, preserving any existing props. */
function stampTitleOnTable(table: Table, title: string): void {
  const data = (table.data ?? {}) as Table['data'] & { hProperties?: Record<string, unknown> };
  const existing = (data.hProperties ?? {}) as Record<string, unknown>;
  data.hProperties = { ...existing, [TABLE_TITLE_DATA_ATTR]: title };
  table.data = data;
}

export const remarkTableTitle: Plugin<[], Root> = () => {
  return tree => {
    visit(tree, 'table', (table: Table, index, parent) => {
      if (typeof index !== 'number' || !parent || !('children' in parent)) return;
      // First-position tables have no predecessor — no title to hoist.
      if (index === 0) return;
      // The previous sibling — if any — is the candidate title paragraph.
      const siblings = parent.children;
      const prev = siblings[index - 1];
      if (prev.type !== 'paragraph') return;
      const title = extractTitleFromParagraph(prev);
      if (title === null) return;
      stampTitleOnTable(table, title);
      // Drop the title paragraph from the parent. The table now sits at
      // `index - 1`; return SKIP + that index so the walker keeps moving
      // forward without re-visiting the same table.
      siblings.splice(index - 1, 1);
      return [SKIP, index];
    });
  };
};
